import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Spinner, ThemeToggle } from './EnvLayout';
import AdminLoginForm from './AdminLoginForm';
import { ui } from './envUi';
import AdminGames from './AdminGames';
import AdminSurvey from './AdminSurvey';
import AdminCarbon from './AdminCarbon';
import AdminResponses from './AdminResponses';
import AdminPlant from './AdminPlant';

const TABS = [
  { id: 'games',     label: 'Games' },
  { id: 'survey',    label: 'Survey' },
  { id: 'carbon',    label: 'CO₂ objects' },
  { id: 'plant',     label: 'Plant' },
  { id: 'responses', label: 'Responses' },
] as const;
type TabId = (typeof TABS)[number]['id'];

/** /environmental/admin — sign in with name / ID / major, then manage everything. */
export default function EnvironmentalAdmin() {
  const utils  = trpc.useUtils();
  const status = trpc.questionnaire.adminStatus.useQuery();
  const [tab, setTab] = useState<TabId>('games');

  if (status.isLoading) return <Spinner />;
  if (!status.data?.admin) return <AdminLogin onDone={() => utils.questionnaire.adminStatus.invalidate()} />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:py-10">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-10 w-10 object-contain" />
          <h1 className="text-xl font-bold">Environmental admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/environmental" className={ui.btnGhost}>View games</Link>
          <LogoutButton onDone={() => utils.questionnaire.adminStatus.invalidate()} />
          <ThemeToggle />
        </div>
      </header>

      <div role="tablist" className="mb-6 flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
        {TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                  className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors
                              ${tab === t.id
                                ? 'border-yellow-500 text-slate-900 dark:text-slate-100'
                                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'games'     && <AdminGames />}
      {tab === 'survey'    && <AdminSurvey />}
      {tab === 'carbon'    && <AdminCarbon />}
      {tab === 'plant'     && <AdminPlant />}
      {tab === 'responses' && <AdminResponses />}
    </div>
  );
}

function LogoutButton({ onDone }: { onDone: () => void }) {
  const logout = trpc.questionnaire.adminLogout.useMutation({ onSuccess: onDone });
  return <button onClick={() => logout.mutate()} disabled={logout.isPending} className={ui.btnGhost}>Sign out</button>;
}

function AdminLogin({ onDone }: { onDone: () => void }) {
  return (
    <div className="flex flex-col items-center px-4 pt-14">
      <div className="absolute right-3 top-3"><ThemeToggle /></div>
      <img src="/logo.png" alt="MrPanadol" className="mb-6 h-24 w-24 object-contain" />
      <AdminLoginForm onSuccess={onDone} />
      <Link to="/environmental" className="mt-6 text-sm text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">← Back to games</Link>
    </div>
  );
}
